import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import { Select } from 'components/FormFields';
import { sortWaivers } from 'pages/app/Waivers/sortWaivers';

const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

// GraphQL query for waivers
const GET_WAIVERS = gql`
  query GetWaiversData {
    getWaivers(type: "waiver") {
      id
      name
    }
  }
`;

// Form card container
const FormCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const FormSection = styled.div`
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '24px')};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionSubtext = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.text?.secondary || '#64748B'};
  margin: -12px 0 20px 0;
  line-height: 1.5;
`;

// Selection list container
const SelectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SelectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SelectWrapper = styled.div`
  flex: 1;
`;

const RemoveButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

const AddButtonWrapper = styled.div`
  margin-top: 12px;

  button {
    width: auto;
    padding: 10px 20px;
  }
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#94A3B8'};
  margin: 0;
  padding: 16px;
  text-align: center;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
  border: 1px dashed
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
`;

// Account defaults banner
const AccountBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.06)'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(250, 70, 22, 0.3)'
        : 'rgba(250, 70, 22, 0.2)'};
  border-radius: 10px;
  margin-bottom: 24px;
`;

const BannerIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.2)'
      : 'rgba(250, 70, 22, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const BannerContent = styled.div`
  flex: 1;
`;

const BannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin-bottom: 4px;
`;

const BannerText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94a3b8' : '#64748b')};
`;

const ApplyButton = styled.div`
  button {
    width: auto;
    padding: 8px 16px;
  }
`;

// Icons
const FileTextIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const BuildingIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" />
    <path d="M16 6h.01" />
    <path d="M12 6h.01" />
    <path d="M12 10h.01" />
    <path d="M12 14h.01" />
    <path d="M16 10h.01" />
    <path d="M16 14h.01" />
    <path d="M8 10h.01" />
    <path d="M8 14h.01" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const XIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MinimumAgeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 10px;
`;

const MinimumAgeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MinimumAgeIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;
`;

const MinimumAgeLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
`;

const MinimumAgeDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.text?.secondary || '#64748B'};
  margin-top: 2px;
`;

const MinimumAgeControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AgeInput = styled.input`
  width: 80px;
  height: 40px;
  padding: 0 12px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.06)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
  border-radius: 8px;
  outline: none;

  &:focus {
    border-color: #fa4616;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background: linear-gradient(135deg, #fa4616, #ff6b3b);
  }

  &:checked + span:before {
    transform: translateX(22px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgb(226, 232, 240)'};
  transition: all 0.3s ease;
  border-radius: 26px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 3px;
    top: 3px;
    background-color: #ffffff;
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const WaiversTab = ({
  waivers = [],
  onWaiversChange,
  accountWaivers = [],
  minimumAge = null,
  onMinimumAgeChange,
  accountMinimumAge = null,
  loading,
}) => {
  const hasMinimumAge = minimumAge !== null && minimumAge !== undefined;
  const [ageInputValue, setAgeInputValue] = useState(
    hasMinimumAge ? String(minimumAge) : ''
  );

  useEffect(() => {
    setAgeInputValue(hasMinimumAge ? String(minimumAge) : '');
  }, [minimumAge, hasMinimumAge]);

  const handleMinimumAgeToggle = (checked) => {
    if (!onMinimumAgeChange) return;
    if (checked) {
      const fallback =
        accountMinimumAge !== null && accountMinimumAge !== undefined
          ? accountMinimumAge
          : 18;
      setAgeInputValue(String(fallback));
      onMinimumAgeChange(fallback);
    } else {
      setAgeInputValue('');
      onMinimumAgeChange(null);
    }
  };

  const handleMinimumAgeInput = (e) => {
    const raw = e.target.value;
    setAgeInputValue(raw);
    if (raw === '') return;
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    if (onMinimumAgeChange) {
      onMinimumAgeChange(Math.max(0, Math.min(120, parsed)));
    }
  };

  const handleMinimumAgeBlur = () => {
    if (ageInputValue === '') {
      const fallback = hasMinimumAge ? minimumAge : 18;
      setAgeInputValue(String(fallback));
      if (onMinimumAgeChange) {
        onMinimumAgeChange(fallback);
      }
    }
  };

  // Fetch all available waivers
  const { data: waiversData } = useQuery(GET_WAIVERS);
  const allWaivers = sortWaivers(waiversData?.getWaivers || []);

  // Check if account has defaults that can be applied
  const hasAccountDefaults = accountWaivers.length > 0;
  const hasNoSelections = waivers.length === 0;

  // Show banner only when event has no waivers AND account has defaults available
  const showAccountDefaultsBanner = hasNoSelections && hasAccountDefaults;

  // Helper to get ID from waiver object or string
  const getWaiverId = (waiver) => {
    if (typeof waiver === 'object' && waiver !== null) {
      return waiver.id?.toString() || '';
    }
    return waiver?.toString() || '';
  };

  // Waiver handlers
  const addWaiver = () => {
    onWaiversChange([...waivers, { id: '', name: '' }]);
  };

  const removeWaiver = (index) => {
    onWaiversChange(waivers.filter((_, i) => i !== index));
  };

  const updateWaiver = (index, selectedId) => {
    const selectedWaiver = allWaivers.find(
      (w) => w.id.toString() === selectedId
    );
    const updated = [...waivers];
    updated[index] = selectedWaiver
      ? { id: selectedWaiver.id, name: selectedWaiver.name }
      : { id: selectedId, name: '' };
    onWaiversChange(updated);
  };

  // Get available waivers (exclude already selected ones)
  const getAvailableWaivers = (currentIndex) => {
    const selectedIds = waivers
      .filter((_, i) => i !== currentIndex)
      .map((w) => getWaiverId(w));
    return allWaivers.filter((w) => !selectedIds.includes(w.id.toString()));
  };

  // Apply account defaults manually
  const applyAccountDefaults = () => {
    if (accountWaivers.length > 0) {
      onWaiversChange(accountWaivers.map((w) => ({ id: w.id, name: w.name })));
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
          Loading waiver settings...
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {/* Account Defaults Banner - Show only when event has no waivers and account has defaults */}
      {showAccountDefaultsBanner && (
        <AccountBanner>
          <BannerIcon>
            <BuildingIcon />
          </BannerIcon>
          <BannerContent>
            <BannerTitle>Account Default Waivers Available</BannerTitle>
            <BannerText>
              This account has {accountWaivers.length} waiver
              {accountWaivers.length > 1 ? 's' : ''} configured. Click to apply
              them to this event.
            </BannerText>
          </BannerContent>
          <ApplyButton>
            <ButtonV2 elevated type="button" onClick={applyAccountDefaults}>
              Apply Defaults
            </ButtonV2>
          </ApplyButton>
        </AccountBanner>
      )}

      {/* Waivers */}
      <FormCard>
        <FormSection noMargin>
          <SectionTitle>
            <FileTextIcon />
            Waivers
          </SectionTitle>
          <SectionSubtext>
            Select the waivers that apply to this event.
          </SectionSubtext>

          <SelectionList>
            {waivers.length === 0 ? (
              <EmptyText>No waivers selected</EmptyText>
            ) : (
              waivers.map((waiver, index) => (
                <SelectionRow key={index}>
                  <SelectWrapper>
                    <Select
                      value={getWaiverId(waiver)}
                      onChange={(e) => updateWaiver(index, e.target.value)}
                      options={[
                        { value: '', label: 'Select a waiver...' },
                        ...getAvailableWaivers(index).map((w) => ({
                          value: w.id.toString(),
                          label: w.name,
                        })),
                        // Include currently selected waiver even if it would be filtered
                        ...(getWaiverId(waiver)
                          ? allWaivers
                              .filter(
                                (w) => w.id.toString() === getWaiverId(waiver)
                              )
                              .map((w) => ({
                                value: w.id.toString(),
                                label: w.name,
                              }))
                          : []),
                      ].filter(
                        (opt, i, arr) =>
                          arr.findIndex((o) => o.value === opt.value) === i
                      )}
                    />
                  </SelectWrapper>
                  <RemoveButtonWrapper>
                    <ButtonV2
                      danger
                      variant="icon"
                      type="button"
                      onClick={() => removeWaiver(index)}
                      title="Remove waiver"
                      icon={<XIcon />}
                    />
                  </RemoveButtonWrapper>
                </SelectionRow>
              ))
            )}
            <AddButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={addWaiver}
                icon={<PlusIcon />}
              >
                Add Waiver
              </ButtonV2>
            </AddButtonWrapper>
          </SelectionList>
        </FormSection>
      </FormCard>

      {!isTicketHoss && (
        <FormCard>
          <FormSection noMargin>
            <SectionTitle>
              <UserIcon />
              Minimum Age
            </SectionTitle>
            <SectionSubtext>
              Restrict who can purchase a pit pass for this event. Overrides the
              account default when set.
              {accountMinimumAge !== null && accountMinimumAge !== undefined
                ? ` Account default: ${accountMinimumAge}.`
                : ''}
            </SectionSubtext>

            <MinimumAgeRow>
              <MinimumAgeInfo>
                <MinimumAgeIconWrap>
                  <UserIcon />
                </MinimumAgeIconWrap>
                <div>
                  <MinimumAgeLabel>
                    Require a minimum age for this event
                  </MinimumAgeLabel>
                  <MinimumAgeDescription>
                    Anyone under this age cannot purchase or be purchased for.
                    Minors handled by a parent are exempt.
                  </MinimumAgeDescription>
                </div>
              </MinimumAgeInfo>
              <MinimumAgeControls>
                {hasMinimumAge && (
                  <AgeInput
                    type="number"
                    min="0"
                    max="120"
                    value={ageInputValue}
                    onChange={handleMinimumAgeInput}
                    onBlur={handleMinimumAgeBlur}
                    onWheel={(e) => e.target.blur()}
                  />
                )}
                <Toggle>
                  <ToggleInput
                    type="checkbox"
                    checked={hasMinimumAge}
                    onChange={(e) => handleMinimumAgeToggle(e.target.checked)}
                  />
                  <ToggleSlider />
                </Toggle>
              </MinimumAgeControls>
            </MinimumAgeRow>
          </FormSection>
        </FormCard>
      )}
    </Card>
  );
};

WaiversTab.propTypes = {
  waivers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  onWaiversChange: PropTypes.func.isRequired,
  accountWaivers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  minimumAge: PropTypes.number,
  onMinimumAgeChange: PropTypes.func,
  accountMinimumAge: PropTypes.number,
  loading: PropTypes.bool,
};

export default WaiversTab;
